import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { EventInventoryRecord, selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { pricingActions } from "@src/store";
import { Button, ButtonVariant } from "../button/Button";
import Checkbox from "../vGrid/Checkbox";
import helperStyles from "@src/components/helpers.module.scss";
import { clx } from "../utils/stringUtils";
import BulkExternalNotesItem from "./BulkExternalNotesItem";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import { TRAITS, TRAIT_STORED_VALUE, normalizeTrait, parseExternalNotes, isKnownTraitOrAlias } from "./traits";
import styles from "./BulkExternalNotes.module.scss";
import { IModalHandle, Modal } from "../modal/Modal";
import { DeliveryClassificationModal } from "@src/components/deliveryClassification/DeliveryClassificationModal";
import { DeliveryClassification, externalNotesChanged } from "@src/utils/deliveryClassification";

type TBulkExternalNotes = {
	handleClose?: () => void;
};

type TState = {
	items: Array<EventInventoryRecord>;
	selectedTraits: Array<string>;
	customNote: string;
};

const deriveTraitsFromItems = (items: Array<EventInventoryRecord>): Array<string> => {
	const found = new Set<string>();
	items.forEach((item) => {
		parseExternalNotes(item.External_Notes).traits.forEach((t) => found.add(t));
	});
	return TRAITS.filter((t) => found.has(t));
};

const BulkExternalNotes = ({ handleClose }: TBulkExternalNotes) => {
	const dispatch = useAppDispatch();

	const items = useAppSelector(selectSelectedInventoryItems);
	const { loading: appendListingNotesLoading } = useAppSelector((state) => state.pricing.appendListingNotes);
	const { loading: updateListingExternalNotesLoading } = useAppSelector((state) => state.pricing.updateListingExternalNotes);

	const classificationModalRef = useRef<IModalHandle>(null);
	const [classificationError, setClassificationError] = useState<string | null>(null);

	const [state, setState] = useState<TState>({
		items,
		selectedTraits: [],
		customNote: "",
	});

	useEffect(() => {
		setState((prev) => ({
			...prev,
			items,
			selectedTraits: deriveTraitsFromItems(items),
		}));
	}, [items]);

	const handleToggleTrait = (trait: string) => {
		setState((prev) => {
			const included = prev.selectedTraits.some((t) => normalizeTrait(t) === normalizeTrait(trait));
			return {
				...prev,
				selectedTraits: included
					? prev.selectedTraits.filter((t) => normalizeTrait(t) !== normalizeTrait(trait))
					: [...prev.selectedTraits, trait],
			};
		});
	};

	const handleSelectAllTraits = () => {
		setState((prev) => ({
			...prev,
			selectedTraits: prev.selectedTraits.length === TRAITS.length ? [] : [...TRAITS],
		}));
	};

	const handleRemoveItem = (id: number) => {
		setState((prev) => ({ ...prev, items: prev.items.filter((x) => x.Listing_ID !== id) }));
	};

	const handleCustomNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setState((prev) => ({ ...prev, customNote: e.target.value }));
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
		e.preventDefault();
		const text = e.clipboardData.getData("text").replace(/\r?\n|\r/g, ", ");
		const { selectionStart, selectionEnd, value } = e.currentTarget;
		const newValue = value.slice(0, selectionStart) + text + value.slice(selectionEnd);
		setState((prev) => ({ ...prev, customNote: newValue }));
	};

	const externalNotesString = useMemo(() => {
		const customParts = state.customNote
			.split(/[;,]/)
			.map((x) => x.trim())
			.filter(Boolean);

		const traitsToSave = state.selectedTraits.map((t) => TRAIT_STORED_VALUE[t] ?? t);
		const cleanedCustom = _.uniq(customParts.filter((p) => !isKnownTraitOrAlias(p)));
		const combined = [...traitsToSave, ...cleanedCustom];
		return combined.join(", ");
	}, [state.selectedTraits, state.customNote]);

	const handleClassifiedSave = async (deliveryClassification: DeliveryClassification) => {
		const [item] = state.items;
		if (!item) return;
		setClassificationError(null);
		try {
			await dispatch(
				pricingActions.updateListingExternalNotes({
					listingIds: [item.Listing_ID],
					externalNotes: externalNotesString,
					appendNotes: false,
					deliveryClassification,
					skipCache: true,
					skipSavingCache: true,
				}),
			).unwrap();
			classificationModalRef.current?.closeModal();
			toast.success("External notes updated.");
			handleClose?.();
		} catch (error) {
			setClassificationError(typeof error === "string" && error ? error : "Couldn't save the external notes.");
		}
	};

	const handleSubmit = async () => {
		if (!state.items.length) return;

		if (state.items.length === 1 && externalNotesChanged(state.items[0].External_Notes, externalNotesString)) {
			setClassificationError(null);
			classificationModalRef.current?.openModal();
			return;
		}

		const listingIds = state.items.map((x) => x.Listing_ID);

		try {
			await dispatch(
				pricingActions.appendListingNotes({
					listingIds,
					internalNotes: null,
					externalNotes: externalNotesString,
				}),
			).unwrap();
			toast.success("External notes updated.");
			handleClose?.();
		} catch {
			toast.error("Failed to update external notes.");
		}
	};

	const mappedItems = state.items.map((x) => <BulkExternalNotesItem key={x.Item_ID} data={x} onRemove={handleRemoveItem} />);

	const renderTrait = (trait: string) => {
		const included = state.selectedTraits.some((t) => normalizeTrait(t) === normalizeTrait(trait));
		return (
			<label key={trait} className={clx(styles.trait, included ? styles.checked : undefined)}>
				<Checkbox isChecked={included} controlSize="m" onChange={() => handleToggleTrait(trait)} />
				{trait}
			</label>
		);
	};

	const allTraitsSelected = state.selectedTraits.length === TRAITS.length;

	return (
		<div className={styles.root}>
			<EventContextHeader padding listings={state.items.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))} />
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={clx(styles.subHeader, helperStyles.ellipsisDiv, styles.mb)}>Custom External Note</div>
					<div className={styles.mb}>
						<textarea
							className={styles.textArea}
							value={state.customNote}
							placeholder="Enter custom external notes here (e.g. Mobile XFER)..."
							onChange={handleCustomNoteChange}
							onPaste={handlePaste}
						/>
					</div>
					<div className={styles.traitsHeader}>
						<div className={clx(styles.subHeader, helperStyles.ellipsisDiv)}>Ticket Traits</div>
						<div className={styles.selectAll} onClick={handleSelectAllTraits}>
							{allTraitsSelected ? "Unselect All" : "Select All"}
						</div>
					</div>
					<div className={styles.traits}>{TRAITS.map(renderTrait)}</div>
					{state.items.length > 1 ? (
						<div className={styles.warning}>
							<div className={styles.warningHeader}>Warning! You have multiple listings selected.</div>
							<div className={styles.warningText}>
								Saving will overwrite any previous external notes saved to these listings.
							</div>
						</div>
					) : null}
				</div>
				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Changes Summary</div>
						<div className={styles.subHeaderSecondary}>Applying to {state.items.length} items</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={clx(styles.secRowSeat, styles.headerSecRowSeat)}>
							<div className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>Sec, Row, Seat</div>
						</div>
						<div className={styles.qty}>Qty</div>
						<div className={styles.splitLabel}>Splits</div>
						<div className={styles.priceLabel}>Price</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					isLoading={appendListingNotesLoading}
					disabled={!state.items.length || appendListingNotesLoading}>
					Change notes
				</Button>
			</div>
			<Modal ref={classificationModalRef}>
				<DeliveryClassificationModal
					externalNotes={externalNotesString}
					saving={updateListingExternalNotesLoading}
					error={classificationError}
					onCancel={() => classificationModalRef.current?.closeModal()}
					onSave={handleClassifiedSave}
				/>
			</Modal>
		</div>
	);
};

export default BulkExternalNotes;
